/**
 * What a battle id actually is, in words — the board's half of it.
 *
 * The panel resolves the same thing in extension-mu/battle.js, and for the same
 * reason: a member is being asked to commit a unit's damage against
 * `6a79f9ca75f7e0a84967dbdf`, which nobody can check against the page they are
 * looking at. The game's own API names the regions a battle is fought over.
 *
 * Best-effort throughout: a name that will not resolve leaves the id and the
 * link exactly as they were, because those are what the product runs on. Names
 * are cached in localStorage because region and country names do not change and
 * a battle's regions do not change once it exists.
 */
const API = "https://api2.warera.io/trpc/";
const STORE_KEY = "muBattleNames";

/** In-flight and resolved, so ten renders of one board make one request. */
const pending = new Map<string, Promise<string | null>>();
const places = new Map<string, string | null>();

function readStore(): Record<string, string> {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function remember(battleId: string, label: string) {
  try {
    const store = readStore();
    store[battleId] = label;
    localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    // A private window or a full store costs one request next time, no more.
  }
}

async function trpc<T>(path: string, input: unknown): Promise<T | null> {
  const res = await fetch(`${API}${path}?input=${encodeURIComponent(JSON.stringify(input))}`);
  if (!res.ok) throw new Error(`${path} ${res.status}`);
  const body = (await res.json()) as { result?: { data?: T } };
  return body?.result?.data ?? null;
}

/** A region's name and its current owner's country code. */
async function place(regionId: string | undefined): Promise<string | null> {
  if (!regionId) return null;
  const known = places.get(regionId);
  if (known !== undefined) return known;
  const region = await trpc<{ name?: string; country?: string }>("region.getById", { regionId });
  if (!region?.name) return null;
  let code: string | null = null;
  if (region.country) {
    try {
      const country = await trpc<{ code?: string }>("country.getCountryById", {
        countryId: region.country,
      });
      code = country?.code ? String(country.code).toUpperCase() : null;
    } catch {
      // The region's own name is already most of the answer.
    }
  }
  const label = code ? `${region.name} (${code})` : region.name;
  places.set(regionId, label);
  return label;
}

/** A tournament side, which is a numbered team rather than a place. */
async function team(tournamentTeamId: string | undefined): Promise<string | null> {
  if (!tournamentTeamId) return null;
  const known = places.get(tournamentTeamId);
  if (known !== undefined) return known;
  const found = await trpc<{ number?: number }>("tournamentTeam.getById", { tournamentTeamId });
  const label = typeof found?.number === "number" ? `team ${found.number}` : null;
  places.set(tournamentTeamId, label);
  return label;
}

/** "Attacker vs defender", or null when the game will not say. */
export async function battleName(battleId: string): Promise<string | null> {
  if (!battleId || !/^[0-9a-f]{24}$/i.test(battleId)) return null;
  const held = pending.get(battleId);
  if (held) return held;

  const job = (async () => {
    const store = readStore();
    if (typeof store[battleId] === "string") return store[battleId];
    const battle = await trpc<{
      type?: string;
      attacker?: { region?: string; tournamentTeam?: string };
      defender?: { region?: string; tournamentTeam?: string };
    }>("battle.getById", { battleId });
    // Two shapes. A war is fought over ground and names its regions; a tournament
    // battle has no ground at all, only numbered teams, and asking it for a
    // region returns nothing — which read as "this battle has no name".
    const [attacker, defender] = await Promise.all([
      battle?.attacker?.region
        ? place(battle.attacker.region)
        : team(battle?.attacker?.tournamentTeam),
      battle?.defender?.region
        ? place(battle.defender.region)
        : team(battle?.defender?.tournamentTeam),
    ]);
    if (!attacker && !defender) return null;
    const pair = attacker && defender ? `${attacker} vs ${defender}` : (attacker ?? defender);
    const label = battle?.type === "tournament" ? `Tournament: ${pair}` : pair;
    if (label) remember(battleId, label);
    return label;
  })().catch(() => null);

  pending.set(battleId, job);
  const resolved = await job;
  // A failed read is not remembered as a permanent "no name": the usual cause is
  // a network that was briefly gone.
  if (resolved === null) pending.delete(battleId);
  return resolved;
}
